import Requester from '@cobliteam/api-lib/src/request/Requester';
import Environment from '@cobliteam/react-environments';
import { MastraClient } from '@mastra/client-js';
import type { AIUploadedFile } from '../types';

const environment = Environment.getInstance().getEnvironment();
const requester = new Requester({ host: environment.mastraApi });

export const MASTRA_BASE_URL = environment.mastraApi;

export const controller = new AbortController();

export const mastraClient = async () => {
  const accessToken = await requester.getValidToken();
  return new MastraClient({
    baseUrl: MASTRA_BASE_URL,
    abortSignal: controller.signal,
    headers: { Authorization: `Bearer ${accessToken}` },
  });
};

export interface MastraFetchOptions extends Omit<RequestInit, 'body'> {
  body?: Record<string, any>;
  stream?: boolean;
}

export const mastraFetch = async (path: string, options: MastraFetchOptions = {}): Promise<Response> => {
  const accessToken = await requester.getValidToken();
  const { body, stream: _stream, ...rest } = options;

  const response = await fetch(`${MASTRA_BASE_URL}${path}`, {
    ...rest,
    headers: {
      'content-type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(rest.headers as Record<string, string>),
    },
    signal: controller.signal,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI-API request failed (${response.status}): ${errorText}`);
  }

  return response;
};

export type UploadAttachmentOptions = {
  threadId?: string;
  resourceId?: string;
};

/**
 * Upload a user attachment to the AI backend, which stores it in S3 and returns the URL.
 *
 * Uses a raw `fetch` with `FormData` on purpose: the shared `Requester`/`CobliAxios` forces
 * `Content-Type: application/json`, which would strip the multipart boundary. Leaving the header
 * unset lets the browser set `multipart/form-data; boundary=…` automatically.
 */
export const uploadAttachment = async (file: File, { threadId, resourceId }: UploadAttachmentOptions = {}): Promise<AIUploadedFile> => {
  const accessToken = await requester.getValidToken();

  const form = new FormData();
  form.append('file', file);
  if (threadId) form.append('threadId', threadId);
  if (resourceId) form.append('resourceId', resourceId);

  const response = await fetch(`${MASTRA_BASE_URL}/api/v1/files/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
    signal: controller.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI file upload failed (${response.status}): ${errorText}`);
  }

  return (await response.json()) as AIUploadedFile;
};

export default mastraClient;
